// The Discord callback landing (?discord_auth=<status>). Every known status
// maps to one honest state with the useful denial or success and its next
// action; unknown strings render the generic sign-in prompt. Never raw JSON.
import { describeDiscordAuth, type DiscordAuthDescription } from '../../api/discordAuth';
import { HonestState, type HonestAction, type HonestStateVariant } from '../../states/HonestState';

export interface CallbackStatusProps {
  readonly status: string | null;
  readonly onDismiss: () => void;
  readonly onUsePassword: () => void;
  readonly onRetryDiscord: () => void;
  // The two challenge statuses hold an access cookie; the callback hands them
  // to the challenge screen instead of offering a password sign-in that would
  // answer 409 or 428 again.
  readonly onOpenChallenge: (kind: 'workspace' | 'password') => void;
}

const UNAVAILABLE: ReadonlySet<string> = new Set(['provider_failed', 'service_unavailable', 'session_unavailable', 'link_unavailable']);

export function variantForCallback(description: DiscordAuthDescription): HonestStateVariant {
  if (description.outcome === 'signed_in') return 'Ready';
  if (description.outcome === 'denied') return 'Permission denied';
  if (UNAVAILABLE.has(description.status)) return 'Provider unavailable';
  return 'Authentication required';
}

export function titleForCallback(description: DiscordAuthDescription): string {
  switch (description.outcome) {
    case 'signed_in': return 'Discord sign-in complete';
    case 'denied': return 'No access with this Discord identity';
    case 'challenge': return 'One more step';
    default: return description.status === 'unlinked' ? 'Discord identity not linked yet' : 'Discord sign-in did not complete';
  }
}

export function CallbackStatus({ status, onDismiss, onUsePassword, onRetryDiscord, onOpenChallenge }: CallbackStatusProps) {
  const description = describeDiscordAuth(status);
  const variant = variantForCallback(description);
  const challengeKind = description.requiresAccessChallenge
    ? (description.status === 'bootstrap_password_replacement_required' ? 'password' : 'workspace')
    : null;
  const actions: HonestAction[] = [];
  if (description.outcome === 'signed_in') actions.push({ label: 'Open the Terminal', onSelect: onDismiss });
  else if (description.outcome === 'denied') actions.push({ label: 'Ask the workspace owner for an invitation', onSelect: onDismiss });
  else if (challengeKind) actions.push({ label: challengeKind === 'password' ? 'Replace the temporary password' : 'Choose a workspace', onSelect: () => onOpenChallenge(challengeKind) });
  else if (description.resumeLinkAfterPasswordSignIn) actions.push({ label: 'Sign in with email and password', onSelect: onUsePassword });
  else if (description.outcome === 'challenge') actions.push({ label: 'Continue with password', onSelect: onUsePassword });
  if (description.outcome !== 'signed_in' && description.outcome !== 'denied') actions.push({ label: 'Try Discord again', onSelect: onRetryDiscord });
  if (description.outcome === 'denied') actions.push({ label: 'Try a different Discord account', onSelect: onRetryDiscord });
  actions.push({ label: 'Dismiss', onSelect: onDismiss });
  const detail = description.verifiedOnLive ? undefined : 'This status is not verified on the live host; if you see it, tell the owner.';
  return (
    <div className="door-hs" data-discord-auth={description.status}>
      <HonestState variant={variant} title={titleForCallback(description)} reason={description.message} nextAction={actions} detail={detail} />
    </div>
  );
}
